import { FormControl, Text } from '@chakra-ui/react';
import mixpanel from 'mixpanel-browser';
import { useCallback, useMemo } from 'react';

import { TaxonomyTrackingEvents } from '@revelio/core';
import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import { getActivityDefaultSelectionsForTaxonomy } from '../../../next/CustomDash/filters/utils/getActivityDefaultSelectionsForTaxonomy';
import { getRoleDefaultSelectionsForTaxonomy } from '../../../next/CustomDash/filters/utils/getRoleDefaultSelectionsForTaxonomy';
import { getSkillDefaultSelectionsForTaxonomy } from '../../../next/CustomDash/filters/utils/getSkillDefaultSelectionsForTaxonomy';
import { ActivityTaxonomyIdSelect } from '../../../shared/components/ActivityTaxonomyIdSelect';
import { RoleTaxonomyIdSelect } from '../../../shared/components/RoleTaxonomyIdSelect';
import SkillTaxonomyIdSelect from '../../../shared/components/SkillTaxonomyIdSelect';
import { useRicsTaxonomyLabels } from '../../../shared/selection-lists/hooks/useRicsTaxonomyLabels';
import {
  resolveAdaptiveLabelFromId,
  resolveAdaptiveLabelFromSelection,
} from '../../components/utils/roleTaxonomy';
import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import {
  PreferenceScope,
  UserPreferenceKey,
  UserPreferenceSubKey,
  type UserPreferences,
} from '../types';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';
import { mapTaxonomyIdToSelection } from '../utils/taxonomy-mapper';
import styles from './preference-editors.module.css';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';
import layoutStyles from './taxonomies-editor.module.css';

export const meta: PreferenceMeta = {
  id: UserPreferenceKey.Taxonomies,
  label: 'Taxonomies',
};

export default function TaxonomiesEditor({
  affectedScope,
  onPreferenceUpdate,
  preferences,
  diff,
}: PreferenceEditorProps) {
  const { labels } = useRicsTaxonomyLabels();
  const roleTaxonomyPref = preferences[UserPreferenceKey.Taxonomies]?.role;
  const skillTaxonomyPref = preferences[UserPreferenceKey.Taxonomies]?.skill;
  const activityTaxonomyPref =
    preferences[UserPreferenceKey.Taxonomies]?.activity;

  const { roleTaxonomyId, roleAdaptiveResolvedLabel, roleIsAdaptive } =
    useMemo(() => {
      // Adaptive is always offered; if explicitly selected in preferences, show 'adaptive'
      if (roleTaxonomyPref === 'adaptive') {
        const resolvedLabel = resolveAdaptiveLabelFromSelection(
          labels,
          preferences[UserPreferenceKey.Taxonomies]
            ?.effectiveRoleTaxonomySelection
        );
        return {
          roleTaxonomyId: undefined,
          roleAdaptiveResolvedLabel: resolvedLabel,
          roleIsAdaptive: true,
        };
      }

      // Prefer the explicit preference value for display
      const source:
        | CustomRoleTaxonomySelection
        | { default: true }
        | 'adaptive'
        | undefined = roleTaxonomyPref;

      // Map explicit default
      if (
        typeof source === 'object' &&
        (source as { default?: boolean })?.default
      ) {
        return {
          roleTaxonomyId: 0,
          roleAdaptiveResolvedLabel: undefined,
          roleIsAdaptive: false,
        };
      }

      // Non-adaptive concrete selection
      let id: number | undefined;
      const src = source as CustomRoleTaxonomySelection | undefined;
      if (src?.taxonomyId) {
        id = src.taxonomyId;
      } else if (src?.ricsK50Id) {
        id = parseInt(src.ricsK50Id as string, 10);
      } else if (src?.ricsK10Id) {
        id = parseInt(src.ricsK10Id as string, 10) + 50; // mapped into 51-60 UI range
      } else {
        id = undefined;
      }
      return {
        roleTaxonomyId: id,
        roleAdaptiveResolvedLabel: undefined,
        roleIsAdaptive: false,
      };
    }, [roleTaxonomyPref, labels, preferences]);

  const { skillTaxonomyId, skillAdaptiveResolvedLabel, skillIsAdaptive } =
    useMemo(() => {
      // Adaptive is always offered; if explicitly selected in preferences, show 'adaptive'
      if (skillTaxonomyPref === 'adaptive') {
        const resolvedLabel = resolveAdaptiveLabelFromId(
          labels,
          preferences[UserPreferenceKey.Taxonomies]
            ?.effectiveSkillTaxonomySelection
        );
        return {
          skillTaxonomyId: undefined,
          skillAdaptiveResolvedLabel: resolvedLabel,
          skillIsAdaptive: true,
        };
      }

      // Prefer the explicit preference value for display
      const source: number | 'adaptive' | undefined = skillTaxonomyPref;

      if (typeof source === 'number') {
        return {
          skillTaxonomyId: source,
          skillAdaptiveResolvedLabel: undefined,
          skillIsAdaptive: false,
        };
      }

      return {
        skillTaxonomyId: undefined,
        skillAdaptiveResolvedLabel: undefined,
        skillIsAdaptive: false,
      };
    }, [skillTaxonomyPref, labels, preferences]);

  // Cheap derivations — no memo ceremony needed (contrast the role memo above,
  // which resolves default selections).
  const activityIsAdaptive = activityTaxonomyPref === 'adaptive';
  const activityTaxonomyId =
    typeof activityTaxonomyPref === 'number' ? activityTaxonomyPref : undefined;
  const activityAdaptiveResolvedLabel = activityIsAdaptive
    ? resolveAdaptiveLabelFromId(
        labels,
        preferences[UserPreferenceKey.Taxonomies]
          ?.effectiveActivityTaxonomySelection
      )
    : undefined;

  const updateRoleTaxonomyPreference = useCallback(
    (role: 'adaptive' | CustomRoleTaxonomySelection | { default: true }) => {
      const updated = {
        ...(preferences[UserPreferenceKey.Taxonomies] || {}),
        role,
      };
      const typedUpdate =
        updated as UserPreferences[UserPreferenceKey.Taxonomies];
      // A role-taxonomy change invalidates any previously resolved adaptive
      // selection; persisting the residue would leak a company-page resolution
      // into other scopes' display (e.g. "Adaptive (Electronics)" on a role
      // page). The live company page re-resolves and republishes it.
      delete typedUpdate.effectiveRoleTaxonomySelection;

      PreferenceBrowserStorage.update(
        PreferenceScope.GLOBAL,
        UserPreferenceKey.Taxonomies,
        typedUpdate
      );

      // The single taxonomy choice always governs role pages: regenerate the
      // ROLE-scope default filters for the new taxonomy (generic when adaptive).
      const selectionForDefaults: CustomRoleTaxonomySelection =
        role === 'adaptive'
          ? { default: true }
          : (role as CustomRoleTaxonomySelection);

      const nextRoleDefaults =
        getRoleDefaultSelectionsForTaxonomy(selectionForDefaults);

      PreferenceBrowserStorage.update(
        PreferenceScope.ROLE,
        UserPreferenceKey.DefaultFilters,
        nextRoleDefaults
      );

      // Dispatch after storage updates so taxonomy-dependent filter transforms can
      // read the latest preference values from session storage.
      onPreferenceUpdate?.(
        UserPreferenceKey.Taxonomies,
        typedUpdate,
        UserPreferenceSubKey.RoleTaxonomy
      );
    },
    [onPreferenceUpdate, preferences]
  );

  const updateSkillTaxonomyPreference = useCallback(
    (skill: 'adaptive' | number | { default: true }) => {
      const updated = {
        ...(preferences[UserPreferenceKey.Taxonomies] || {}),
        skill,
      } as UserPreferences[UserPreferenceKey.Taxonomies];
      // See the role path: a change invalidates prior adaptive resolution.
      delete updated.effectiveSkillTaxonomySelection;

      PreferenceBrowserStorage.update(
        PreferenceScope.GLOBAL,
        UserPreferenceKey.Taxonomies,
        updated
      );

      // The single taxonomy choice always governs skill pages: regenerate the
      // SKILL-scope default filters for the new taxonomy (generic when
      // adaptive).
      const skillTaxonomyIdForDefaults =
        typeof skill === 'number' && !Number.isNaN(skill) ? skill : 0;
      PreferenceBrowserStorage.update(
        PreferenceScope.SKILL,
        UserPreferenceKey.DefaultFilters,
        getSkillDefaultSelectionsForTaxonomy(skillTaxonomyIdForDefaults)
      );

      // Dispatch after storage updates so taxonomy-dependent filter transforms can
      // read the latest preference values from session storage.
      onPreferenceUpdate?.(
        UserPreferenceKey.Taxonomies,
        updated,
        UserPreferenceSubKey.SkillTaxonomy
      );
    },
    [onPreferenceUpdate, preferences]
  );

  const updateActivityTaxonomyPreference = useCallback(
    (activity: 'adaptive' | number) => {
      const updated = {
        ...(preferences[UserPreferenceKey.Taxonomies] || {}),
        activity,
      } as UserPreferences[UserPreferenceKey.Taxonomies];
      delete updated.effectiveActivityTaxonomySelection;

      PreferenceBrowserStorage.update(
        PreferenceScope.GLOBAL,
        UserPreferenceKey.Taxonomies,
        updated
      );

      // Activity pages follow the same taxonomy choice: regenerate the
      // ACTIVITY-scope default filters for it (generic when adaptive).
      const activityTaxonomyIdForDefaults =
        typeof activity === 'number' && !Number.isNaN(activity) ? activity : 0;
      PreferenceBrowserStorage.update(
        PreferenceScope.ACTIVITY,
        UserPreferenceKey.DefaultFilters,
        getActivityDefaultSelectionsForTaxonomy(activityTaxonomyIdForDefaults)
      );

      onPreferenceUpdate?.(
        UserPreferenceKey.Taxonomies,
        updated,
        UserPreferenceSubKey.ActivityTaxonomy
      );
    },
    [onPreferenceUpdate, preferences]
  );

  const handleRoleChange = useCallback(
    (newTaxonomyId: number | 'adaptive') => {
      mixpanel.track(TaxonomyTrackingEvents.TAXONOMY_SELECTION_CHANGED, {
        taxonomy_type: 'role',
        taxonomy_id: String(newTaxonomyId),
        is_adaptive: newTaxonomyId === 'adaptive',
        previous_is_adaptive: roleTaxonomyPref === 'adaptive',
        // Taxonomy is stored GLOBAL, but track the page the change was made on
        // (origin-page dimension) — matches the PREFERENCE_CHANGED save event.
        preference_scope: affectedScope,
      });
      // Map id into v3 selection object (0 means explicit default selection)
      if (newTaxonomyId === 0) {
        updateRoleTaxonomyPreference({ default: true });
        return;
      } else if (newTaxonomyId === 'adaptive') {
        updateRoleTaxonomyPreference('adaptive');
        return;
      }

      const selection = mapTaxonomyIdToSelection(newTaxonomyId);
      updateRoleTaxonomyPreference(selection);
    },
    [updateRoleTaxonomyPreference, roleTaxonomyPref, affectedScope]
  );

  const handleSkillChange = useCallback(
    (newTaxonomyId: number | 'adaptive') => {
      // Normalise NaN -> 0 before tracking so the recorded value matches what
      // updateSkillTaxonomyPreference actually stores.
      const effectiveId =
        typeof newTaxonomyId === 'number' && Number.isNaN(newTaxonomyId)
          ? 0
          : newTaxonomyId;
      mixpanel.track(TaxonomyTrackingEvents.TAXONOMY_SELECTION_CHANGED, {
        taxonomy_type: 'skill',
        taxonomy_id: String(effectiveId),
        is_adaptive: effectiveId === 'adaptive',
        previous_is_adaptive: skillTaxonomyPref === 'adaptive',
        // See handleRoleChange: track the origin page, not the storage scope.
        preference_scope: affectedScope,
      });
      if (effectiveId === 'adaptive') {
        updateSkillTaxonomyPreference('adaptive');
        return;
      }
      updateSkillTaxonomyPreference(effectiveId);
    },
    [updateSkillTaxonomyPreference, skillTaxonomyPref, affectedScope]
  );

  const handleActivityChange = useCallback(
    (newTaxonomyId: number | 'adaptive') => {
      mixpanel.track(TaxonomyTrackingEvents.TAXONOMY_SELECTION_CHANGED, {
        taxonomy_type: 'activity',
        taxonomy_id: String(newTaxonomyId),
        is_adaptive: newTaxonomyId === 'adaptive',
        previous_is_adaptive: activityTaxonomyPref === 'adaptive',
        preference_scope: affectedScope,
      });
      updateActivityTaxonomyPreference(newTaxonomyId);
    },
    [updateActivityTaxonomyPreference, activityTaxonomyPref, affectedScope]
  );

  return (
    <div
      className={layoutStyles.taxonomiesEditor}
      data-testid="taxonomies-editor"
    >
      <FormControl>
        <PreferenceEditorSectionHeading
          title="Role Taxonomy"
          indicateDiff={
            diff?.includes(UserPreferenceSubKey.RoleTaxonomy) ?? false
          }
        />
        <div
          className={layoutStyles.roleTaxonomyIdSelect}
          data-testid="role-taxonomy-id-select"
        >
          <RoleTaxonomyIdSelect
            value={roleIsAdaptive ? 'adaptive' : roleTaxonomyId}
            includeAdaptive={true}
            currentResolvedAdaptiveLabel={roleAdaptiveResolvedLabel}
            variant="default"
            usePortal={false}
            includeCustom={true}
            onChange={handleRoleChange}
          />
        </div>
        <Text className={styles.description}>
          Choose how job roles are categorized everywhere in the dashboard.{' '}
          <strong>Adaptive</strong> uses our proprietary clustering to group
          similar job titles, and applies where a company selection anchors it —
          other pages use Generic. <strong>Generic</strong> provides standard
          role categories. <strong>Custom</strong> taxonomies are tailored to
          specific company naming conventions.
        </Text>
      </FormControl>
      <FormControl>
        <PreferenceEditorSectionHeading
          title="Skill Taxonomy"
          indicateDiff={
            diff?.includes(UserPreferenceSubKey.SkillTaxonomy) ?? false
          }
        />
        <div
          className={layoutStyles.roleTaxonomyIdSelect}
          data-testid="skill-taxonomy-id-select"
        >
          <SkillTaxonomyIdSelect
            value={skillIsAdaptive ? 'adaptive' : skillTaxonomyId}
            includeAdaptive={true}
            currentResolvedAdaptiveLabel={skillAdaptiveResolvedLabel}
            variant="default"
            usePortal={false}
            includeCustom={true}
            onChange={handleSkillChange}
          />
        </div>
        <Text className={styles.description}>
          Select how skills are organized everywhere in the dashboard.{' '}
          <strong>Adaptive</strong> groups related skills using our clustering
          algorithm, and applies where a company selection anchors it — other
          pages use Generic. <strong>Generic</strong> offers standard skill
          groupings. <strong>Custom</strong> taxonomies reflect company-specific
          skill frameworks and terminology.
        </Text>
      </FormControl>
      <FormControl>
        <PreferenceEditorSectionHeading
          title="Activity Taxonomy"
          indicateDiff={
            diff?.includes(UserPreferenceSubKey.ActivityTaxonomy) ?? false
          }
        />
        <div
          className={layoutStyles.roleTaxonomyIdSelect}
          data-testid="activity-taxonomy-id-select"
        >
          <ActivityTaxonomyIdSelect
            value={activityIsAdaptive ? 'adaptive' : activityTaxonomyId}
            currentResolvedAdaptiveLabel={activityAdaptiveResolvedLabel}
            variant="default"
            usePortal={false}
            onChange={handleActivityChange}
          />
        </div>
        <Text className={styles.description}>
          Choose how work activities are grouped everywhere in the dashboard.{' '}
          <strong>Adaptive</strong> picks the taxonomy that matches your company
          or industry selection, and other pages use Generic.{' '}
          <strong>Generic</strong> provides standard activity categories.
        </Text>
      </FormControl>
    </div>
  );
}
